import { createApi } from "@reduxjs/toolkit/query/react";
import { adminBaseQuery } from "@/store/baseQuery";
import type {
  StatisticsByConditionsDataDto, StatisticsByActivitiesDataDto,
  Last24HoursDataDto, JourneyAnalyticsResponse, OrganizationDoctorDto,
} from "@healthlinks/ui/analytics/types";

export interface AnalyticsScope {
  organizationId: string;
  profileIds: string[];
}
export interface AnalyticsParams extends AnalyticsScope {
  from?: string;
  to?: string;
  journeyId?: string;
  granularity?: "daily" | "weekly";
}
export interface AnalyticsOrganization {
  id: number;
  name: string;
}
export interface AnalyticsOrganizationSearch {
  data: AnalyticsOrganization[];
  meta: { has_more: boolean };
}

export interface AnalyticsFilters {
  doctors: OrganizationDoctorDto[];
}

export const analyticsQueryString = (params: AnalyticsParams): string => {
  const query = new URLSearchParams();
  if (params.organizationId !== "all") query.set("organization_id", params.organizationId);
  params.profileIds.forEach((id) => query.append("profile_ids[]", id));
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  if (params.journeyId && params.journeyId !== "all") query.set("journey_id", params.journeyId);
  if (params.granularity) query.set("granularity", params.granularity);
  return query.toString();
};

export const analyticsApi = createApi({
  reducerPath: "analyticsApi",
  baseQuery: adminBaseQuery,
  endpoints: (builder) => ({
    searchAnalyticsOrganizations: builder.query<AnalyticsOrganizationSearch, { search: string; page: number }>({
      query: ({ search, page }) => `/analytics/organizations?${new URLSearchParams({ search, page: String(page) })}`,
    }),
    getAnalyticsFilters: builder.query<AnalyticsFilters, string>({
      query: (organizationId) => `/analytics/filters?${analyticsQueryString({ organizationId, profileIds: [] })}`,
    }),
    getConditionAnalytics: builder.query<StatisticsByConditionsDataDto, AnalyticsParams>({
      query: (params) => `/analytics/conditions?${analyticsQueryString(params)}`,
    }),
    getSendAnalytics: builder.query<StatisticsByActivitiesDataDto, AnalyticsParams>({
      query: (params) => `/analytics/activities?${analyticsQueryString(params)}`,
    }),
    getHourlyAnalytics: builder.query<Last24HoursDataDto, AnalyticsScope>({
      query: (params) => `/analytics/last-24-hours?${analyticsQueryString(params)}`,
    }),
    getAdminJourneyAnalytics: builder.query<JourneyAnalyticsResponse, AnalyticsParams>({
      query: (params) => `/analytics/journeys?${analyticsQueryString(params)}`,
    }),
  }),
});

export const {
  useSearchAnalyticsOrganizationsQuery,
  useGetAnalyticsFiltersQuery, useGetConditionAnalyticsQuery, useGetSendAnalyticsQuery,
  useGetHourlyAnalyticsQuery, useGetAdminJourneyAnalyticsQuery,
} = analyticsApi;
